import { execa } from 'execa';

export interface ChangedFile {
  path: string;
  status: 'added' | 'modified' | 'deleted' | 'renamed';
}

export async function getChangedFiles(cwd: string, base?: string): Promise<ChangedFile[]> {
  try {
    // Try to get diff against base branch or HEAD
    const baseRef = base ?? await getDefaultBranch(cwd);
    const mergeBase = await getMergeBase(cwd, baseRef);

    const result = await execa('git', ['diff', '--name-status', mergeBase], {
      cwd,
      reject: false,
    });

    if (result.exitCode !== 0) {
      // Fallback to uncommitted changes
      return await getUncommittedChanges(cwd);
    }

    return parseGitDiff(result.stdout);
  } catch {
    // Not a git repo or git not available
    return [];
  }
}

export async function getUncommittedChanges(cwd: string): Promise<ChangedFile[]> {
  try {
    // Get staged + unstaged changes
    const result = await execa('git', ['status', '--porcelain'], {
      cwd,
      reject: false,
    });

    if (result.exitCode !== 0) {
      return [];
    }

    return parseGitStatus(result.stdout);
  } catch {
    return [];
  }
}

export async function getStagedFiles(cwd: string): Promise<ChangedFile[]> {
  try {
    const result = await execa('git', ['diff', '--name-status', '--cached'], {
      cwd,
      reject: false,
    });

    if (result.exitCode !== 0) {
      return [];
    }

    return parseGitDiff(result.stdout);
  } catch {
    return [];
  }
}

async function getDefaultBranch(cwd: string): Promise<string> {
  try {
    // Try to get default branch from remote
    const result = await execa('git', ['remote', 'show', 'origin'], {
      cwd,
      reject: false,
    });

    const match = result.stdout.match(/HEAD branch: (.+)/);
    if (match) {
      return `origin/${match[1]}`;
    }
  } catch {
    // Ignore
  }

  // Fallback to common defaults
  try {
    await execa('git', ['rev-parse', '--verify', 'origin/main'], { cwd });
    return 'origin/main';
  } catch {
    try {
      await execa('git', ['rev-parse', '--verify', 'origin/master'], { cwd });
      return 'origin/master';
    } catch {
      return 'HEAD~1';
    }
  }
}

async function getMergeBase(cwd: string, base: string): Promise<string> {
  try {
    const result = await execa('git', ['merge-base', 'HEAD', base], {
      cwd,
      reject: false,
    });

    if (result.exitCode === 0 && result.stdout.trim()) {
      return result.stdout.trim();
    }
  } catch {
    // Ignore
  }

  return base;
}

function parseGitDiff(output: string): ChangedFile[] {
  const files: ChangedFile[] = [];

  for (const line of output.split('\n')) {
    if (!line.trim()) continue;

    const [status, ...pathParts] = line.split('\t');
    const path = pathParts.join('\t');

    if (!path) continue;

    let fileStatus: ChangedFile['status'];
    switch (status?.[0]) {
      case 'A':
        fileStatus = 'added';
        break;
      case 'M':
        fileStatus = 'modified';
        break;
      case 'D':
        fileStatus = 'deleted';
        break;
      case 'R':
        fileStatus = 'renamed';
        break;
      default:
        fileStatus = 'modified';
    }

    files.push({ path, status: fileStatus });
  }

  return files;
}

function parseGitStatus(output: string): ChangedFile[] {
  const files: ChangedFile[] = [];

  for (const line of output.split('\n')) {
    if (!line.trim()) continue;

    const status = line.slice(0, 2);
    const path = line.slice(3);

    if (!path) continue;

    let fileStatus: ChangedFile['status'];
    if (status.includes('A') || status === '??') {
      fileStatus = 'added';
    } else if (status.includes('D')) {
      fileStatus = 'deleted';
    } else if (status.includes('R')) {
      fileStatus = 'renamed';
    } else {
      fileStatus = 'modified';
    }

    files.push({ path, status: fileStatus });
  }

  return files;
}

export function filterResultsByFiles(
  results: Array<{ file?: string }>,
  changedFiles: ChangedFile[]
): typeof results {
  const changedPaths = new Set(changedFiles.map((f) => f.path));

  return results.filter((r) => {
    if (!r.file) return true; // Keep results without file (general issues)
    return changedPaths.has(r.file);
  });
}
